// Pricing & packaging strategy for Clarus Partners, rendered in /admin → Pricing.
// Block types: p, h3, list, table, callout - rendered by DocBlocks.jsx.

export const PRICING_STRATEGY_META = {
  title: "Clarus Partners - Pricing & Packaging Strategy",
  subtitle: "Goal: $5k/month within 3 months · 10-20 hrs/week · 3-5 retainer clients + one-off flow",
  updated: "Prepared after TPB, ASIC and CPA Australia verification (2025-26 figures)",
};

export const PRICING_STRATEGY = [
  {
    id: "critique",
    title: "1. Overall critique & key decisions",
    blocks: [
      { type: "p", text: "The four-tier ladder ($1k → $2k → $3k → $4k) is fundamentally right: clean steps, each tier a clear identity (keep up → close out → control → advise). The names are good. What needs fixing is what's INSIDE the tiers, not the prices." },
      { type: "h3", text: "What's wrong with the draft" },
      {
        type: "list",
        items: [
          "AP/AR follow-up sits in the CHEAPEST tier. Chasing bills and invoices is the most open-ended, creep-prone task in bookkeeping - it can't live in an uncapped $1,000 tier. Moved to Growth with a 20-document monthly cap.",
          "Payroll in Growth is uncapped. Payroll for 15 staff at $2k/month is a loss-maker. Capped at 5 employees, with a per-employee add-on above that.",
          "No fair-use limits anywhere. Without transaction caps, a 900-transaction trades business and a 60-transaction consultant pay the same $1,000. Caps added to every tier.",
          "Essentials and Growth overlap: both imply reconciled books. Resolved by identity: Essentials = transactions coded and reconciled monthly; Growth = a real month-end CLOSE with review, reporting pack and a call.",
          "'Email support' was undefined in the draft. Per your decision: same-day response on ALL tiers, with unlimited email and phone support limited to the scope of the service provided. The tiers now differentiate on scheduled check-ins and deliverables, not access - put a 15-minute timebox on casual phone calls in the engagement letter so Essentials stays profitable.",
          "Partner at $4k is well below market for genuine fractional CFO work ($5k-$15k/month is common), but it is correctly priced for your segment IF the days are capped (1.5-2 days/month). State the cap openly - buyers respect it.",
        ],
      },
      { type: "h3", text: "Key decisions (all reflected in the site copy)" },
      {
        type: "list",
        items: [
          "Keep all four tiers and all four price points - the gaps are defensible because each step adds a capability, not just volume.",
          "Quote '+ GST' everywhere. Your clients are GST-registered businesses who claim it back; quoting +GST keeps your margin intact when you register.",
          "The Xero Health Check stays FREE for now as the front door - switch it to $495 (credited toward a Clean-Up Sprint) once you have 3-5 reviews/case studies. [NIMA TO CONFIRM]",
          "The Clean-Up Sprint is the bridge: from $1,000, fixed-quoted after the health check, with the timeframe set by what the health check finds. Every clean-up ends with a retainer offer at 50% off the first month. [NIMA TO CONFIRM]",
          "New Business Launch Pack bundle: company + business name registration ($1,295 incl ASIC fees) + Xero setup ($300) = $1,595, positioned as 'save the clean-up later'.",
          "3-month minimum term on retainers, 30-day notice, monthly direct debit in advance, 10% annual prepay discount.",
        ],
      },
    ],
  },
  {
    id: "tiers",
    title: "2. The tiers - full definition",
    blocks: [
      { type: "p", text: "Public-facing inclusions are on the website pricing section. The caps, exclusions and response times below go in the engagement letter and the client service guide." },
      {
        type: "table",
        headers: ["", "Essentials - $1,000/mo + GST", "Growth - $2,000/mo + GST", "Control - $3,000/mo + GST", "Partner - $4,000/mo + GST"],
        rows: [
          ["Who it's for", "Owner who just needs the books kept right and accountant-ready", "Business with staff that wants the month properly closed and reported", "Growing business making decisions on the numbers", "Startup/business wanting senior finance judgement without a hire"],
          ["Core", "Transaction coding to agreed chart, monthly bank & credit card reconciliations, receipt chasing via capture app, one-page monthly P&L summary", "Everything in Essentials + month-end close checklist, management pack (P&L, balance sheet, cash summary + plain-English commentary), AP/AR processing, payroll & super", "Everything in Growth + 13-week rolling cashflow forecast, budgets vs actuals, job/project profitability, app integrations & automation", "Everything in Control + KPI dashboard, board/investor pack, pricing & scenario modelling, strategic planning"],
          ["Calls & support", "Email & phone within scope (no scheduled calls)", "1 × 30-min monthly call", "Fortnightly 30-min call", "Up to 2 meetings/month + priority queue"],
          ["Response time", "Same business day", "Same business day", "Same business day", "Same business day, first in queue"],
          ["Fair-use caps", "≤150 transactions/mo, 1 entity, 2 bank/credit accounts, no employees", "≤300 transactions/mo, ≤20 AP/AR documents, payroll ≤5 employees (+$15/employee above)", "≤600 transactions/mo, ≤40 AP/AR documents, payroll ≤15 employees, 1 integration included", "≤600 transactions/mo, payroll ≤15 employees, deliverables capped at ~2 days/month of senior time"],
          ["Excluded", "Payroll, AP/AR chasing, calls, BAS/tax lodgement (always)", "Cashflow forecasting, job costing, integrations", "Board/investor reporting, strategy", "Anything beyond the 2-day monthly cap - quoted separately"],
        ],
      },
      { type: "h3", text: "Scope creep & upgrades" },
      {
        type: "list",
        items: [
          "Transaction counts measured from the Xero Transaction Detail report; reviewed quarterly.",
          "Two consecutive months over a cap → propose the next tier or a fixed-price variation. Never silently absorb it.",
          "Out-of-scope requests (e.g. a one-off cleanup of a second entity) quoted fixed-price before work starts, or at $180/hour. [NIMA TO CONFIRM hourly rate]",
          "Casual phone/email questions are welcome on every tier but capped at ~15 minutes within the plan's scope - anything bigger is quoted work. Same-day response applies on all tiers; Partner jumps the queue.",
          "Downgrades allowed at month-end after the 3-month minimum; upgrades any time, pro-rata.",
        ],
      },
    ],
  },
  {
    id: "maths",
    title: "3. Hours per client & the $5k/month maths",
    blocks: [
      {
        type: "table",
        headers: ["Tier", "Est. hours/client/month", "Price", "Effective $/hour"],
        rows: [
          ["Essentials", "~3.5h (coding & recs 2h, P&L 0.5h, email/admin 1h)", "$1,000", "~$285"],
          ["Growth", "~7.5h (Essentials 3.5h + payroll 1.5h + close 1h + pack 1h + call 0.5h)", "$2,000", "~$265"],
          ["Control", "~11.5h (Growth 7.5h + forecast 1.5h + budgets/job costing 1.5h + extra call 1h)", "$3,000", "~$260"],
          ["Partner", "~16.5h (Control 11.5h + board pack 2h + strategy 2h + meetings 1h)", "$4,000", "~$240"],
        ],
      },
      {
        type: "callout",
        text: "All tiers clear a $200/hour floor, so nothing is structurally underpriced - PROVIDED the caps hold. The two watch-items: Essentials creeps through unscheduled phone calls (timebox to ~15 minutes within scope, quote anything bigger), and Partner creeps past 2 days if board prep expands (re-quote when it does).",
      },
      { type: "h3", text: "Does $5k/month fit in 10-20 hrs/week?" },
      {
        type: "list",
        items: [
          "10-20 hrs/week ≈ 45-85 hrs/month of capacity.",
          "Fastest path: 1 × Growth + 1 × Control = $5,000/month at ~19 hours/month - leaving 25-65 hours/month for clean-up sprints and onboarding.",
          "Balanced book: 1 Essentials + 2 Growth + 1 Control = $8,000/month at ~38 hours/month - still inside 10 hrs/week.",
          "Full book: 1 of each tier = $10,000/month at ~39 hours/month.",
          "Conclusion: the constraint is sales pipeline, not capacity. Even 2-3 clients hit the goal with most of your week free for one-off Clean-Up Sprints ($2.5k-$6k each).",
        ],
      },
    ],
  },
  {
    id: "one-offs",
    title: "4. One-off products - prices, scope, timeframes",
    blocks: [
      {
        type: "table",
        headers: ["Product", "Price", "Scope", "Timeframe", "Role"],
        rows: [
          ["Xero Health Check", "Free (intro) → $495 later [NIMA TO CONFIRM]", "15-point diagnostic, written report, prioritised fix list", "Up to 48 hours, depending on business size", "FRONT DOOR - no-brainer first step, feeds everything"],
          ["Xero Clean-Up Sprint", "From $1,000 - fixed quote after health check", "Historical recs up to date, errors/duplicates/suspense fixed, opening balances & payroll liabilities corrected, handover pack for the tax accountant", "Set by health check findings", "BRIDGE - ends with a retainer offer (50% off month 1) [NIMA TO CONFIRM]"],
          ["Company + business name registration", "$1,295 including ASIC fees ($611 company + $45 business name/1yr; +$62 if name reservation needed)", "ASIC lodgements, ABN/TFN/GST/PAYG registrations, constitution, share setup, registers. General process info only - structure advice referred to their accountant/lawyer", "5-7 business days", "Front door for new businesses"],
          ["Xero setup (add-on)", "$300 with registration; $750 standalone [NIMA TO CONFIRM standalone price]", "Organisation setup, chart of accounts, GST settings per the client's registered agent, bank feeds, invoice branding, receipt capture", "3-5 business days", "Bundle glue"],
          ["New Business Launch Pack", "$1,595 (registration + Xero setup)", "Everything above + free 90-day health check", "Up to 3 days", "BUNDLE - 'start clean, never need a clean-up'"],
          ["Payroll compliance audit", "$950 fixed (≤10 employees)", "Pay items, super rates & payable balances, leave accruals, STP settings - findings report for the client's agent to act on", "1 week", "Trust builder for Growth tier"],
          ["System / integration setup", "From $1,200 per app, fixed quote", "e.g. Simpro/ServiceM8/Stripe/POS → Xero, mapping, testing, training handover", "Confirmed once scope and app are locked in", "Feeds Control tier"],
          ["EOFY accountant handover pack", "$750 fixed (free for Growth+ clients)", "Year-end reconciled file, workpapers index, adjustments list for the tax accountant", "1 week", "Loyalty + July revenue"],
        ],
      },
    ],
  },
  {
    id: "terms",
    title: "5. Terms - setup fees, minimums, notice, prepay",
    blocks: [
      {
        type: "list",
        items: [
          "Onboarding: no separate setup fee IF the client starts with a Health Check or Clean-Up Sprint (that IS the onboarding). For a client with a genuinely clean file, a $495 onboarding diagnostic applies. This quietly enforces the front door.",
          "Minimum term: 3 months on all retainers, then rolling monthly. Justified openly: 'month one is getting set up properly; we ask for three to show you the rhythm'.",
          "Notice: 30 days written notice either side after the minimum term.",
          "Billing: monthly in advance by direct debit (use your Xero + a debit service). No invoices-in-arrears for retainers.",
          "Annual prepay: 10% discount (12 months for the price of ~10.8). Offer it AFTER 3 months of proven fit, not at signing.",
          "Annual price review each July, CPI-linked - put it in the engagement letter now so it isn't a negotiation later.",
        ],
      },
    ],
  },
  {
    id: "layout",
    title: "6. Pricing page layout (as built on the site)",
    blocks: [
      {
        type: "list",
        items: [
          "Section 1 - Monthly support: 4 tier cards, each with tier name, one-line promise, 'From $X/mo + GST', 5-6 scannable inclusions, and a CTA. Growth carries the 'Most popular' badge (it's the tier you actually want to sell - best value-per-hour for you and the first tier with a real close + call).",
          "Section 2 - Comparison table: features down the side, tiers across the top, ticks and short values. This is what serious buyers screenshot and send to their partner/accountant.",
          "Section 3 - One-off projects: Health Check, Clean-Up Sprint, Launch Pack, payroll audit, integrations, EOFY pack - each with fixed or 'from' price and timeframe.",
          "Section 4 - Reassurance strip: fixed quotes before work starts · 3-month minimum, then monthly · no hourly meters · accountant-ready, we don't lodge.",
          "Essentials is deliberately the weakest-value card (no calls, email only) - price anchoring pushes buyers to Growth, which is where you want them.",
        ],
      },
    ],
  },
  {
    id: "compliance",
    title: "7. Compliance & risk items to check",
    blocks: [
      { type: "h3", text: "TPB / BAS service boundary (verified against TPB guidance)" },
      {
        type: "list",
        items: [
          "Under the Tax Agent Services Act 2009, a 'BAS service' is ascertaining or advising on liabilities/obligations/entitlements under a BAS provision (GST, PAYG withholding/instalments, FBT, fuel tax credits etc.) where the client can reasonably be expected to RELY on it - for a fee. Penalties for unregistered provision: up to $82,500 (individual).",
          "TPB guidance says these are NOT BAS services: data entry, bank reconciliations, payment processing, and coding transactions under the supervision and control of a registered tax/BAS agent.",
          "The risk point in your tiers is independent GST coding that flows into a lodged BAS. 'Accountant-ready books' framing helps but is not a shield on its own - reliance is a question of fact.",
          "Path (confirmed): Clarus Partners will NOT register as a tax agent or BAS agent. Every engagement uses the supervision wording: GST coding follows the client's registered agent's instructions, and the agent reviews before any lodgement. This wording is now locked into the engagement letter template.",
          "Practical consequence: your sales calls should set the expectation that clients need a registered tax/BAS agent in the loop (most already have one). If a prospect has no accountant, partner with 1-2 tax accountants you trust and refer - this also feeds the referral channel.",
          "Website wording stays as-is and is safe: 'we prepare accurate, reconciled, accountant-ready books - we don't lodge BAS or tax returns'. Never write 'we prepare your BAS', 'GST advice', or 'BAS-ready' anywhere.",
        ],
      },
      { type: "h3", text: "Company registration work" },
      {
        type: "list",
        items: [
          "You CAN: lodge ASIC forms, reserve names, set up standard share structures and registers, apply for ABN/TFN/GST/PAYG via the Business Registration Service, and give factual/process information.",
          "You CANNOT: advise on whether a company is the right structure (tax/legal advice), draft bespoke constitutions, or advise on share classes for tax outcomes - refer these to the client's accountant or lawyer and say so in the service description.",
          "Add to the Launch Pack fine print: 'We handle the registrations and setup. Choosing a business structure involves tax and legal considerations - we'll flag the questions to ask your accountant or lawyer, but we don't provide that advice.'",
        ],
      },
      { type: "h3", text: "GST on your fees, insurance, engagement letters" },
      {
        type: "list",
        items: [
          "GST registration is compulsory at $75,000 annual turnover. At $5k/month you're at $60k - expect to cross it, so register from the start and quote +GST. [NIMA TO CONFIRM current GST registration]",
          "CPA Australia members providing public accounting services must hold a Public Practice Certificate and PI insurance per By-Law 9.8 (minimum $2m cover in Australia; run-off cover when you cease). Since you advertise publicly and will exceed the <$10k mini-PII threshold, sort this before launch. [NIMA TO CONFIRM PPC + PI status]",
          "Check your other employment contract for outside-practice, non-compete and conflict clauses - the car-dealership exclusion suggests this matters. Get written consent if required. [NIMA TO CONFIRM]",
          "Engagement letter per client (CPA Australia has templates for members): scope per tier, fair-use caps, reliance/lodgement boundary wording, limitation of liability, direct debit authority, privacy clause, 30-day notice, CPI review clause.",
        ],
      },
    ],
  },
  {
    id: "questions",
    title: "8. Questions I need answered to tighten this",
    blocks: [
      { type: "h3", text: "Priority 1 - compliance blockers" },
      {
        type: "list",
        items: [
          "DONE: BAS/tax agent registration - you will not register; supervision wording is locked into the engagement letter.",
          "Do you already hold a CPA Public Practice Certificate and PI insurance? Is the business registered for GST?",
          "Does your other employment contract permit this practice, and do you need written consent?",
        ],
      },
      { type: "h3", text: "Priority 2 - pricing decisions" },
      {
        type: "list",
        items: [
          "Health Check: keep it free until you have 3-5 reviews, then $495 credited to a clean-up - agree?",
          "Clean-Up Sprint from $1,000 (fixed quote after health check) and 50%-off-first-month retainer offer - agree?",
          "Standalone Xero setup at $750 - agree? Ad-hoc hourly rate of $180 - your floor?",
        ],
      },
      { type: "h3", text: "Priority 3 - packaging polish" },
      {
        type: "list",
        items: [
          "Payroll caps (5 in Growth, 15 in Control) and +$15/employee above - workable for your target clients?",
          "Publish exact tier pricing on the site (current build) or show 'from' only?",
          "Annual prepay at 10% - comfortable with the cashflow vs discount trade-off?",
        ],
      },
    ],
  },
];

// One-page client-facing service guide, rendered under the strategy in /admin → Pricing.
export const SERVICE_GUIDE_META = {
  title: "Clarus Partners - Services & Pricing Guide",
  subtitle: "Remote Xero bookkeeping and fractional CFO support for Australian small business · Nima Riazati, CPA",
};

export const SERVICE_GUIDE = [
  {
    id: "promise",
    title: "What we do",
    blocks: [
      { type: "p", text: "We keep your Xero file accurate, reconciled and accountant-ready - so your registered tax or BAS agent can lodge without the back-and-forth, and you always know where the business stands. Clarus Partners does not lodge BAS or tax returns; we prepare the books so the people who do can do it quickly." },
    ],
  },
  {
    id: "monthly",
    title: "Monthly support plans",
    blocks: [
      {
        type: "table",
        headers: ["", "Essentials", "Growth", "Control", "Partner"],
        rows: [
          ["From", "$1,000/mo + GST", "$2,000/mo + GST", "$3,000/mo + GST", "$4,000/mo + GST"],
          ["Best for", "Keeping the books right", "Closing the month properly", "Running the business on the numbers", "Senior finance on tap"],
          ["Coding & reconciliations", "✓", "✓", "✓", "✓"],
          ["Monthly P&L summary", "✓", "✓", "✓", "✓"],
          ["Month-end close + report pack", "-", "✓", "✓", "✓"],
          ["Payroll & super", "-", "✓", "✓", "✓"],
          ["AP/AR processing", "-", "✓", "✓", "✓"],
          ["Cashflow forecast & budgets", "-", "-", "✓", "✓"],
          ["Job/project profitability", "-", "-", "✓", "✓"],
          ["Integrations & automation", "-", "-", "✓", "✓"],
          ["KPI dashboard & board pack", "-", "-", "-", "✓"],
          ["Scheduled check-ins", "-", "Monthly call", "Fortnightly call", "2 meetings/mo"],
          ["Support within plan scope", "Email & phone, same-day response", "Email & phone, same-day response", "Email & phone, same-day response", "Email & phone, same-day priority"],
        ],
      },
      { type: "p", text: "All plans: fixed monthly fee, 3-month minimum then rolling monthly with 30 days' notice, no hourly meters. Every plan includes same-day support by email and phone, limited to the scope of your service. Fair-use limits apply and are set out in your engagement letter - if your volume grows past a tier, we'll tell you before it costs you anything." },
    ],
  },
  {
    id: "once-off",
    title: "One-off projects",
    blocks: [
      {
        type: "table",
        headers: ["Project", "Price", "Timeframe"],
        rows: [
          ["Xero Health Check - 15-point diagnostic with a prioritised fix list", "Free (introductory)", "Up to 48 hours, depending on business size"],
          ["Xero Clean-Up Sprint - historical catch-up, accountant-ready handover", "From $1,000, fixed quote after health check", "Set by health check findings"],
          ["New Business Launch Pack - company + business name registration (incl. ASIC fees) plus Xero setup", "$1,595", "Up to 3 days"],
          ["Payroll compliance audit (≤10 employees)", "$950", "1 week"],
          ["System / app integration setup", "From $1,200 per app", "Confirmed once scope and app are locked in"],
          ["EOFY accountant handover pack", "$750 (free for Growth plan and above)", "1 week"],
        ],
      },
    ],
  },
  {
    id: "how",
    title: "How working together starts",
    blocks: [
      {
        type: "list",
        items: [
          "1. Book a free 30-minute call - tell us what's going on with your file.",
          "2. Free Xero Health Check - a written diagnosis and a fixed quote before any work starts.",
          "3. We fix it, then keep it clean - most clients move onto a monthly plan after their clean-up.",
        ],
      },
      { type: "p", text: "Contact: nima@claruspartners.com.au · claruspartners.com.au · Remote, Australia-wide" },
    ],
  },
];
